/**
 * Removing this device's cached copies of server data.
 *
 * Signing out clears the token, which stops this browser *asking* the server
 * for anything — and left every row it had already been given sitting in
 * IndexedDB, readable by anyone who opens the developer tools or the next
 * person to use the laptop. The replica made that a copy of the whole ledger
 * rather than a handful of recently-viewed pages, which is what turns an
 * untidiness into something worth doing properly.
 *
 * What is deleted is only ever a *copy*: rows, cached documents, field
 * snapshots. Losing them costs a resync and nothing else.
 *
 * What is deliberately NOT deleted is the outbox. It holds work this person
 * did and the server has not accepted yet — a transaction entered on a train
 * is not a cache, and throwing it away on sign-out would be data loss dressed
 * up as hygiene. It stays, encrypted no differently from before, and syncs
 * when they sign in again. `pendingWork` is how a caller can say so first.
 */

const PREFIX = 'colplat-';
const OUTBOX = 'colplat-outbox-';

/** The databases one app keeps for cached server data — never the outbox. */
function cachesOf(appId: string, env: string): string[] {
  return [
    `${PREFIX}rows-${appId}-${env}`,       // Dataset: the row replica
    `${PREFIX}doccache-${appId}-${env}`,   // DocCache: whole REST responses
    `${PREFIX}${appId}-${env}`,            // Replica: field snapshots
  ];
}

function deleteDatabase(name: string): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    // Resolve on every outcome, including `blocked` — another tab holding the
    // database open must not leave sign-out hanging on a promise that never
    // settles. The delete completes when that tab goes.
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

/**
 * Delete every cached copy this device holds, and report what went.
 *
 * `appIds` is the fallback list for browsers without `indexedDB.databases()`
 * (Firefox has never shipped it). Where enumeration works it is preferred,
 * because it also catches an app this build has never heard of — one the
 * person used before a deploy removed it, say, whose rows would otherwise
 * stay for ever.
 */
export async function clearCachedData(
  env: string, appIds: readonly string[] = [],
): Promise<string[]> {
  let names: string[];
  const list = (indexedDB as { databases?: () => Promise<Array<{ name?: string }>> }).databases;
  if (typeof list === 'function') {
    const found = await list.call(indexedDB).catch(() => [] as Array<{ name?: string }>);
    names = found
      .map((d) => d.name ?? '')
      .filter((n) => n.startsWith(PREFIX) && !n.startsWith(OUTBOX) && n.endsWith(`-${env}`));
  } else {
    names = [...new Set(appIds.flatMap((app) => cachesOf(app, env)))];
  }
  await Promise.all(names.map(deleteDatabase));
  return names;
}

/** Whether anything this person did is still waiting to reach the server. */
export async function pendingWork(appIds: readonly string[], env: string): Promise<boolean> {
  const list = (indexedDB as { databases?: () => Promise<Array<{ name?: string }>> }).databases;
  if (typeof list !== 'function') return false;
  const found = await list.call(indexedDB).catch(() => [] as Array<{ name?: string }>);
  const outboxes = found
    .map((d) => d.name ?? '')
    .filter((n) => n.startsWith(OUTBOX) && n.endsWith(`-${env}`));
  if (outboxes.length === 0) return false;
  void appIds;
  for (const name of outboxes) {
    const has = await new Promise<boolean>((resolve) => {
      // Opened at whatever version is already there: passing one could
      // trigger an upgrade, and a question must not migrate anything.
      const req = indexedDB.open(name);
      req.onerror = () => resolve(false);
      req.onsuccess = () => {
        const db = req.result;
        const stores = [...db.objectStoreNames];
        if (stores.length === 0) { db.close(); resolve(false); return; }
        let left = stores.length;
        let any = false;
        const tx = db.transaction(stores, 'readonly');
        for (const store of stores) {
          const count = tx.objectStore(store).count();
          count.onsuccess = () => {
            if (count.result > 0) any = true;
            left -= 1;
            if (left === 0) { db.close(); resolve(any); }
          };
          count.onerror = () => {
            left -= 1;
            if (left === 0) { db.close(); resolve(any); }
          };
        }
      };
    });
    if (has) return true;
  }
  return false;
}
